'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MediaSource, Slide } from '@/shared/types';
import MediaDisplay, { type MediaDisplayRef } from '../ui/mediaDisplay';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Spinner } from '@heroui/spinner';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { useCsamAccess } from '@/lib/csam-access';
import { getCurrentFileInfo } from '@/shared/clientFunctions';
import { resolveFullS3Url } from '@/shared/functions';
import { slideMediaInstanceScope } from '@/components/ui/pdfViewerPageHandoff';
import { useTimePilotT } from '@/i18n/useTimePilotT';

/** Prev/next media nav caret (icon + a little breathing room). MediaDisplay's own
 *  responsive side/below collapse (player.tsx, SIDE_TRANSCRIPT_MIN_CONTAINER_WIDTH_PX)
 *  measures its *own* rendered width, which these carets shrink simply by sharing the
 *  same flex row — event-modal UnifiedSources reserves the equivalent width so its
 *  carets don't do the same. Passed to MediaDisplay as narrowLayoutWidthAllowancePx so
 *  a multi-media slide's beside preference isn't silently defeated purely by caret
 *  width (Bugbot follow-up, TR-1589). */
const MEDIA_PANE_NAV_ARROW_RESERVE_PX = 40;

/** Ceiling on how long the pane withholds a clip while content moderation is still
 *  unknown. Provenance file-info carries no request timeout, so without a bound a
 *  stalled lookup would leave the pane spinning for the rest of the session. */
const CONTENT_MODERATION_LOOKUP_MAX_WAIT_MS = 8000;

// Interfaces and types
interface MediaPaneProps {
  slide: Slide;
  isPresentation?: boolean;
  sourcesExpanded?: boolean;
  hasDescription?: boolean;
  isFullscreen: boolean;
  /** Read-only view of the shared item, used to tell whether this pane still drives it. */
  fullscreenMediaItem: MediaSource | null;
  setFullscreenMediaItem: (mediaItem: MediaSource | null) => void;
  mediaDisplayRef?: React.RefObject<MediaDisplayRef>;
  presentationHeight?: number;
  /** Measured width of the slot, so presentation fit does not wait on a late parent read. */
  containerWidth?: number;
  /**
   * Persist a media-context patch for the clip at `mediaIndex`.
   * Writes only that row. Does not touch `sourceContext`.
   */
  onUpdateMediaContext?: (mediaIndex: number, patch: Record<string, unknown>) => void;
  /** Page changes for this clip. Other clips on the slide are not included. */
  onViewingPageChange?: (page: number, mediaIndex: number) => void;
}

function resolveEmbeddedContentModerationType(media: MediaSource | undefined): string | undefined {
  const cm = media?.fileInfo?.contentModeration;
  if (cm?.type && cm.findingStatus !== 'REVIEWED') {
    return cm.type;
  }
  return undefined;
}

// Component definition
const MediaPane: React.FC<MediaPaneProps> = ({ slide, sourcesExpanded, hasDescription, isFullscreen, fullscreenMediaItem, setFullscreenMediaItem, mediaDisplayRef, presentationHeight, containerWidth, onUpdateMediaContext, onViewingPageChange }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [currentMedia, setCurrentMedia] = useState<MediaSource | undefined>(slide.media?.[currentMediaIndex || 0] || undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [showObfuscatedPreview, setShowObfuscatedPreview] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const [fittedContentHeight, setFittedContentHeight] = useState<number | undefined>();
  const [contentModerationType, setContentModerationType] = useState<string | undefined>();
  const [contentModerationPending, setContentModerationPending] = useState(false);
  const t = useTimePilotT();

  const currentSlideKeyRef = useRef<string | Slide | undefined>(undefined);
  /** Read in the sync effect below without making it re-run on every item change. */
  const fullscreenMediaItemRef = useRef<MediaSource | null>(fullscreenMediaItem);
  fullscreenMediaItemRef.current = fullscreenMediaItem;
  const contentModerationMediaRef = useRef<MediaSource | undefined>(undefined);
  const onUpdateMediaContextRef = useRef(onUpdateMediaContext);
  onUpdateMediaContextRef.current = onUpdateMediaContext;
  const currentMediaIndexRef = useRef(currentMediaIndex);
  currentMediaIndexRef.current = currentMediaIndex;
  const contentModerationAnsweredRef = useRef(false);
  const contentModerationDeadlineRef = useRef(0);
  const { cellId, accessToken } = useCellServiceSession();
  const { canSeeCsamTags, canRevealOriginal } = useCsamAccess();
  const resolvedContentModerationType = contentModerationType;

  const updateMediaContext = useCallback((field: string | object, value?: unknown) => {
    const handler = onUpdateMediaContextRef.current;
    if (!handler) return;
    const patch = typeof field === 'string' ? { [field]: value } : field;
    handler(currentMediaIndexRef.current, patch as Record<string, unknown>);
  }, []);

  const instanceScope = slideMediaInstanceScope(slide.id, currentMediaIndex);

  const displayMedia = useMemo(() => {
    if (!currentMedia || !instanceScope) return currentMedia;
    if (currentMedia.captureCitationScope === instanceScope) return currentMedia;
    return { ...currentMedia, captureCitationScope: instanceScope };
  }, [currentMedia, instanceScope]);

  // A saved capture replaces the slide object but keeps the event id. Reset only
  // when the event itself changes, so the open clip does not jump back to the
  // first file. Slides without an id still reset on a new object, which is how
  // two events that share a displayOrder stay distinct.
  // Done during render rather than in an effect because effects for the slide
  // change all run in the same commit: an effect-scheduled reset lands a render
  // too late, so the sync effect below would resolve the new slide's media at
  // the previous slide's index.
  const slideChangeKey = slide.id ?? slide;
  if (slideChangeKey !== currentSlideKeyRef.current) {
    currentSlideKeyRef.current = slideChangeKey;
    setCurrentMediaIndex(0);
  }

  useEffect(() => {
    setIsLoading(true);
    const revealTimer = setTimeout(() => {
      setIsLoading(false);
    }, 10);
    return () => clearTimeout(revealTimer);
  }, [slideChangeKey]);

  useEffect(() => {
    const nextMedia = slide.media?.[currentMediaIndex];
    setCurrentMedia(nextMedia);

    // When not fullscreen, leave fullscreenMediaItem unchanged so mediaDisplay can read
    // the exited item's page number.
    if (!isFullscreen || !setFullscreenMediaItem) return;

    // Fullscreen being on does not mean this pane opened it. The event modal's thumbnail
    // editor turns it on over the still-mounted presentation and supplies its own row, and
    // overwriting that row with this slide's clip is what made every Displayed Media item
    // open the slide's first file (TR-3228). Maximizing from the pane needs no push here
    // either, since mediaDisplay.toggleFullScreen sets the item as it goes fullscreen. So
    // only supply a clip when nothing else has: that is tp-presentation's hotkey path,
    // which turns fullscreen on bare when it has no MediaDisplay ref to toggle.
    if (fullscreenMediaItemRef.current) return;

    const scope = slideMediaInstanceScope(slide.id, currentMediaIndex);
    setFullscreenMediaItem(
      nextMedia
        ? (scope && nextMedia.captureCitationScope !== scope
          ? { ...nextMedia, captureCitationScope: scope }
          : nextMedia)
        : null,
    );
  }, [slide.media, slide.id, currentMediaIndex, isFullscreen]);

  // Discover content moderation for the active clip the same way chat SourceCard /
  // UnifiedSources do: prefer the embedded file-info block, otherwise ask provenance
  // file-info.
  useEffect(() => {
    // Both pieces of state describe one clip, so clear them the moment the clip changes
    // rather than when the lookup below resolves — otherwise the previous clip's type
    // drives the Obfuscated/Original switcher and CM-gated capture labeling for the new
    // one. Keyed on the clip (not every effect run) so a token refresh mid-viewing does
    // not drop a resolved type or snap a reviewer's "Original" selection back.
    if (currentMedia !== contentModerationMediaRef.current) {
      contentModerationMediaRef.current = currentMedia;
      contentModerationAnsweredRef.current = false;
      contentModerationDeadlineRef.current = 0;
      setShowObfuscatedPreview(true);
      setContentModerationType(undefined);
    }

    // Marks this clip's moderation question settled, so a later re-run of this effect for
    // the same clip (a token refresh mid-viewing) doesn't pull an already-rendered clip
    // back behind the spinner — that would remount MediaDisplay and reset player state.
    const markAnswered = () => {
      contentModerationAnsweredRef.current = true;
      setContentModerationPending(false);
    };

    // Nothing to ask right now, but the question isn't settled either: the flag can turn
    // on and the session can resolve while the clip stays the same. Deliberately leaves
    // the answered ref alone so the lookup those changes unblock can still withhold the
    // clip — treating these as answers would let a flagged clip render unlabeled.
    const releaseWithoutAnswering = () => {
      setContentModerationPending(false);
    };

    if (!canSeeCsamTags || !currentMedia) {
      setContentModerationType(undefined);
      releaseWithoutAnswering();
      return;
    }

    const embedded = resolveEmbeddedContentModerationType(currentMedia);
    if (embedded) {
      setContentModerationType(embedded);
      markAnswered();
      return;
    }

    if (!cellId || !accessToken) {
      // A type already resolved for this clip outlives a momentary gap in the session
      // (token refresh); only a still-unanswered clip falls back to "nothing known".
      if (!contentModerationAnsweredRef.current) {
        setContentModerationType(undefined);
      }
      releaseWithoutAnswering();
      return;
    }

    const lookupUrl = resolveFullS3Url(
      currentMedia,
      currentMedia.s3Url || currentMedia.mediaUrl || currentMedia.mediaSrc || currentMedia.originalUrl || '',
    );
    if (!lookupUrl) {
      setContentModerationType(undefined);
      markAnswered();
      return;
    }

    let cancelled = false;
    let revealTimer: ReturnType<typeof setTimeout> | undefined;
    // Withhold the clip until the lookup answers. A flagged clip rendered while its
    // type is still unknown gets no Obfuscated/Original switcher and stores captures
    // unlabeled, which is exactly what the switcher exists to prevent.
    if (!contentModerationAnsweredRef.current) {
      setContentModerationPending(true);
      // Deadline rather than a fresh delay per run, so repeated re-runs while the answer
      // is still outstanding can't keep pushing the reveal further out.
      if (!contentModerationDeadlineRef.current) {
        contentModerationDeadlineRef.current = Date.now() + CONTENT_MODERATION_LOOKUP_MAX_WAIT_MS;
      }
      revealTimer = setTimeout(
        () => {
          if (!cancelled) markAnswered();
        },
        Math.max(0, contentModerationDeadlineRef.current - Date.now()),
      );
    }

    void getCurrentFileInfo(lookupUrl, cellId, accessToken).then((info) => {
      if (cancelled) return;
      // getCurrentFileInfo answers null on error as well as on "no such file", so a
      // failed re-lookup (token refresh mid-viewing) says nothing about the clip. Reading
      // it as "clean" would strip obfuscation off a flagged clip already on screen; a
      // real answer still gets through, including one that reports it since REVIEWED.
      if (!info && contentModerationAnsweredRef.current) {
        markAnswered();
        return;
      }
      const cm = info?.contentModeration;
      setContentModerationType(
        cm?.type && cm.findingStatus !== 'REVIEWED' ? cm.type : undefined,
      );
      markAnswered();
    });

    return () => {
      cancelled = true;
      clearTimeout(revealTimer);
    };
  }, [canSeeCsamTags, currentMedia, cellId, accessToken]);

  // The pane is a fixed, overflow-hidden box. Subtracting a flat chrome guess
  // (and flooring it at 120) can hand MediaDisplay a height taller than the
  // real content area once padding, the border, or the attribution row are
  // accounted for, which clips the playback controls (TR-3842).
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || presentationHeight == null) {
      setFittedContentHeight(undefined);
      return;
    }
    const measure = () => {
      const next = Math.floor(el.clientHeight);
      setFittedContentHeight((prev) => (prev === next ? prev : next > 0 ? next : undefined));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [presentationHeight, slide.evidenceSrc, slide.mediaType, contentModerationPending, currentMediaIndex, isLoading]);

  if (!slide.media || slide.media.length === 0 || isLoading) {
    return null;
  }
  const showLeftArrow = !isFullscreen && slide.media.length > 1;
  const showRightArrow = !isFullscreen && slide.media.length > 1;
  const arrowReservePx =
    (showLeftArrow ? MEDIA_PANE_NAV_ARROW_RESERVE_PX : 0) +
    (showRightArrow ? MEDIA_PANE_NAV_ARROW_RESERVE_PX : 0);
  const hasCm = !!resolvedContentModerationType;
  return (
    <motion.div
      className={`mediaPane
        box-border flex max-w-full overflow-x-auto sm:overflow-x-hidden min-h-full
        ${presentationHeight ? 'h-full flex-none sm:overflow-hidden' : 'sm:min-h-0 sm:flex-1 sm:overflow-hidden'}
        custom-scrollbar2 sm:min-w-[330px] w-full
        ${!slide.mediaSrc && !slide.media?.[0]?.mediaSrc ? 'hidden' : 'slide-boxes m-0 p-2'}
        ${isFullscreen ?
            'relative top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center' :
            `relative flex flex-col justify-start items-center
            ${!slide.slideDescription ? 'w-full' : ''}`
        }
        min-h-0
      `}
      style={{
        // slide-boxes paints a 20% ink wash, and theme-core is not global here,
        // so a bare --surface-inverse is transparent and the slide copy shows
        // through the video (TR-3842).
        backgroundColor: 'var(--surface-inverse, #0f1d32)',
        ...(presentationHeight
          ? { height: presentationHeight, minHeight: presentationHeight }
          : {}),
      }}
        animate={{
          scale: isFullscreen ? 1 : 1,
        }}
        transition={{ duration: 0.3 }}
    >
      <div
        ref={contentRef}
        className={`mediaPaneContent flex flex-row items-center w-full min-h-0 box-border ${presentationHeight ? 'flex-1' : ''}`}
      >
        {!isFullscreen && slide.media.length > 1 && (
          <Button
            type="button"
            iconOnly
            size="sm"
            style="text"
            color="light"
            background="inverse"
            aria-label={t('sources.previousMedia')}
            className="flex shrink-0 justify-start cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed"
            onClick={() => setCurrentMediaIndex(currentMediaIndex - 1)}
            disabled={currentMediaIndex === 0}
          >
            <CaretLeftIcon />
          </Button>
        )}
        {contentModerationPending && (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-4 min-h-[200px]"
            data-testid="mediaPaneModerationPending"
          >
            <Spinner size="lg" color="white" className="opacity-70" />
            <div className="animate-pulse font-bold">{t('sources.loadingInfo')}</div>
          </div>
        )}
        {!contentModerationPending && !!slide.media && slide.media.length > 0 && !!currentMedia && (
          <MediaDisplay
            ref={mediaDisplayRef ?? undefined}
            mediaItem={displayMedia}
            isPresentation={true}
            containerHeight={fittedContentHeight}
            containerWidth={containerWidth}
            updateMediaContext={onUpdateMediaContext ? updateMediaContext : undefined}
            commitViewChoiceOnClose={Boolean(onUpdateMediaContext)}
            sourcesExpanded={sourcesExpanded && !hasDescription}
            narrowLayoutWidthAllowancePx={arrowReservePx}
            contentModerationType={hasCm ? resolvedContentModerationType : undefined}
            showObfuscatedPreview={hasCm ? showObfuscatedPreview : undefined}
            onToggleObfuscatedPreview={
              hasCm && canRevealOriginal ? setShowObfuscatedPreview : undefined
            }
            viewingPageScope={instanceScope}
            onViewingPageChange={
              onViewingPageChange
                ? (page) => onViewingPageChange(page, currentMediaIndex)
                : undefined
            }
          />
        )}
        {!isFullscreen && slide.media.length > 1 && (
          <Button
            type="button"
            iconOnly
            size="sm"
            style="text"
            color="light"
            background="inverse"
            aria-label={t('sources.nextMedia')}
            className="flex shrink-0 justify-end cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed"
            onClick={() => setCurrentMediaIndex(currentMediaIndex + 1)}
            disabled={currentMediaIndex === slide.media.length - 1}
          >
            <CaretRightIcon />
          </Button>
        )}
      </div>
      {(slide.evidenceSrc || slide.mediaType === 'youtube') && <motion.div
        className='flex flex-row m-0 p-0 italic font-light text-sm gap-2 text-white/30'
        animate={{
          opacity: isFullscreen ? 0.5 : 1,
          y: isFullscreen ? 20 : 0
        }}
      >
        {slide.evidenceSrc && (
          <label className='font-bold'>{t('sources.sourceColon')}</label>
        )}
        <span>
          {slide.mediaType === 'youtube'
            ? slide.mediaSrc || slide.media?.[0]?.mediaSrc
            : slide.evidenceSrc}
        </span>
      </motion.div>}
    </motion.div>
  );
}

export default MediaPane;
